import { css } from "@emotion/react";
import { useTailwindMediaQueries } from "@/hooks/useTailwindMediaQueries";
import type { ImmutableObject } from "@/lib/immutable";
import type { PackageItem } from "../../../hooks/useInventoryState";
import {
	type ShoppingCartItemModel,
	useShoppingCartItemsState,
} from "../../../hooks/useShoppingCartItemsState";
import { useTranslate } from "../../../hooks/useTranslate/useTranslate";
import type { ShoppingCartViewSettings } from "../ShoppingCartControl";
import { ShoppingCartPackageItemIncludedDesktop } from "./ShoppingCartPackageItemIncludedDesktop";
import { ShoppingCartPackageItemOptionalDesktop } from "./ShoppingCartPackageItemOptionalDesktop";

//------------------------------------------------------------------------------------
export type ShoppingCartPackageItemsDesktopProps = {
	shoppingCartItem: ShoppingCartItemModel;
	index: number;
	viewSettings: ShoppingCartViewSettings;
};
export const ShoppingCartPackageItemsDesktop: React.FC<
	ShoppingCartPackageItemsDesktopProps
> = (props) => {
	const translate = useTranslate();
	const mediaQueries = useTailwindMediaQueries();
	const product = props.shoppingCartItem.product;
	const packageItems = product.PackageItems ?? [];
	const shoppingCartItemsState = useShoppingCartItemsState();
	const enableQty = shoppingCartItemsState.getQty(product) > 0;
	const showDailyRate =
		mediaQueries.tablet &&
		props.viewSettings.hasRates &&
		props.viewSettings.hasDailyRate;
	const showWeeklyRate =
		mediaQueries.tablet &&
		props.viewSettings.hasRates &&
		props.viewSettings.hasWeeklyRate;
	const showMonthlyRate =
		mediaQueries.tablet &&
		props.viewSettings.hasRates &&
		props.viewSettings.hasMonthlyRate;

	const includedPackageItems: ImmutableObject<PackageItem>[] = [];
	const optionalPackageItems: ImmutableObject<PackageItem>[] = [];
	if (packageItems) {
		for (const packageItem of packageItems) {
			if (packageItem.IsOption) {
				optionalPackageItems.push(packageItem);
			} else {
				includedPackageItems.push(packageItem);
			}
		}
		packageItems.map((item) => {
			if (item.IsOption) {
				return (
					<ShoppingCartPackageItemOptionalDesktop
						key={item.InventoryId}
						packageProduct={product}
						packageItem={item}
						enableQty={enableQty}
						viewSettings={props.viewSettings}
					/>
				);
			}
			return null;
		});
	}

	const styles = css`
        display:flex;
        flex-wrap: wrap;
        align-items: flex-start;
        table.packageitems {
            margin:.25rem .5rem;
            font-size: .8rem;
            border-collapse: collapse;
        }
        @media only screen and (max-width: 720px) {
            table.packageitems {
                width: 100%;
            }
        }
        @media only screen and (min-width: 721px) {
            table.packageitems {
                min-width: 650px;
            }
        }
        tr.headerrow td {
            padding: 0 .25rem .1rem .25rem;
            white-space: no-wrap;
            font-weight: bold;
        }
        tbody.included tr.headerrow {
            border-bottom: 1px solid #000000;
        }
        tbody.optional tr.headerrow {
            border-bottom: 1px solid #000000;
        }
        tr.packageitemrow {
            font-size:.7rem;
        }
        .col-image {
            padding: 0 .25rem;
        }
        .col-description {
            width: 100%;
            padding: 0 .5rem;
            min-width: 150px;
        }
        tr.headerrow td.col-qty {
            text-align: center;
            min-width: 6rem;
        }
        tr.packageitemrow td.col-qty {
            text-align: center;
            min-width: 6rem;
        }
        td.col-qty .MuiInputBase-root {
            text-align: right;
        }
        .col-dailyrate
        {
            text-align: right;
            padding: 0 .5em;
        }
        .col-weeklyrate {;
            text-align: right;
            padding: 0 .5em;
        }
        .col-monthlyrate {
            text-align: right;
            padding: 0 .5em;
        }
    `;

	return (
		<>
			{product && (
				<div css={styles}>
					{/* Package Includes */}
					{includedPackageItems.length > 0 && (
						<table className="packageitems included">
							<tbody className="included">
								<tr className="headerrow">
									<td className="col-description" colSpan={2}>
										{translate("Package includes")}
									</td>
									<td className="col-qty">{translate("Quantity")}</td>
									{showDailyRate && (
										<td className="col-dailyrate">{translate("Daily")}</td>
									)}
									{showWeeklyRate && (
										<td className="col-weeklyrate">{translate("Weekly")}</td>
									)}
									{showMonthlyRate && (
										<td className="col-monthlyrate">{translate("Monthly")}</td>
									)}
								</tr>
								{includedPackageItems.map((item) => {
									if (item.IsOption) return null;
									return (
										<ShoppingCartPackageItemIncludedDesktop
											key={item.InventoryId}
											packageProduct={product}
											packageItem={item}
											enableQty={enableQty}
											viewSettings={props.viewSettings}
										/>
									);
								})}
							</tbody>
						</table>
					)}
					{optionalPackageItems.length > 0 && (
						<table className="packageitems optional">
							<tbody className="optional">
								<tr className="headerrow">
									<td className="col-description" colSpan={2}>
										{translate("Optional accessories")}
									</td>
									<td className="col-qty">{translate("Quantity")}</td>
									{showDailyRate && (
										<td className="col-dailyrate">{translate("Daily")}</td>
									)}
									{showWeeklyRate && (
										<td className="col-weeklyrate">{translate("Weekly")}</td>
									)}
									{showMonthlyRate && (
										<td className="col-monthlyrate">{translate("Monthly")}</td>
									)}
								</tr>
								{optionalPackageItems.map((item) => {
									if (item.IsOption) {
										return (
											<ShoppingCartPackageItemOptionalDesktop
												key={item.InventoryId}
												packageProduct={product}
												packageItem={item}
												enableQty={enableQty}
												viewSettings={props.viewSettings}
											/>
										);
									}
									return null;
								})}
							</tbody>
						</table>
					)}
				</div>
			)}
		</>
	);
};
